import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Card, CardContent, CardHeader, CircularProgress, Box, Stack, Typography, IconButton, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ScienceIcon from '@mui/icons-material/Science';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFetch } from '../../../hooks/use-fetch';
import { BASE_ADMIN_API_URL } from '../../../config';
import dayjs from 'dayjs';
import { SESSION_TAGS } from './tags';

function descendingComparator(a, b, orderBy) {
    const va = a[orderBy];
    const vb = b[orderBy];
    if (vb < va) return -1;
    if (vb > va) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

const TAG_LABEL_MAP = Object.fromEntries(SESSION_TAGS.map(t => [t.value, t.labelKey]));

const columns = [
    { id: 'created_at', label: 'SESSIONS.DATE', width: 160 },
    { id: 'device_id', label: 'SESSIONS.DEVICE', width: 180 },
    { id: 'type', label: 'SESSIONS.TYPE', width: 110 },
    { id: 'tags', label: 'SESSIONS.TAGS', width: 200 },
    { id: 'recorded_by_name', label: 'SESSIONS.RECORDED_BY', width: 140 },
    { id: 'notes', label: 'SESSIONS.NOTES' },
];

function TypeCell({ type }) {
    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            {type === 'STREAM'
                ? <FiberManualRecordIcon sx={{ fontSize: 14, color: 'error.main' }} />
                : <ScienceIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
            <span>{type || '—'}</span>
        </Stack>
    );
}

export default function SessionsList() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('created_at');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const fetchWithAuth = useFetch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/sessions`);
                if (!response.ok) throw new Error(await response.text());
                const data = await response.json();
                if (!cancelled) setSessions(data);
            } catch (e) {
                console.error('Failed to load sessions', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [fetchWithAuth]);

    const handleSort = useCallback((columnId) => {
        setOrder(prev => orderBy === columnId && prev === 'asc' ? 'desc' : 'asc');
        setOrderBy(columnId);
    }, [orderBy]);

    const handleClick = useCallback((id) => {
        navigate(`${id}/details`);
    }, [navigate]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteTarget) return;
        try {
            const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/sessions/${deleteTarget}`, { method: 'DELETE' });
            if (response.ok || response.status === 204) {
                setSessions(prev => prev.filter(s => s.id !== deleteTarget));
            }
        } catch (e) {
            console.error('Failed to delete session', e);
        }
        setDeleteTarget(null);
    }, [deleteTarget, fetchWithAuth]);

    const sorted = useMemo(() => {
        const rows = sessions.map(s => ({
            ...s,
            device_id: s.device?.id || s.device_id,
            recorded_by_name: s.recorded_by?.name || '',
        }));
        return rows.sort(getComparator(order, orderBy));
    }, [sessions, order, orderBy]);

    return (
        <Card>
            <CardHeader title={t('SESSIONS.LIST_HEADER')} />
            <CardContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map(col => (
                                        <TableCell key={col.id} sx={{ width: col.width }}>
                                            <TableSortLabel
                                                active={orderBy === col.id}
                                                direction={orderBy === col.id ? order : 'asc'}
                                                onClick={() => handleSort(col.id)}
                                            >
                                                {t(col.label)}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ width: 48 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sorted.map(session => (
                                    <TableRow
                                        key={session.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleClick(session.id)}
                                    >
                                        <TableCell>{dayjs(session.created_at).format('YYYY-MM-DD HH:mm')}</TableCell>
                                        <TableCell>{session.device_id || '—'}</TableCell>
                                        <TableCell><TypeCell type={session.type} /></TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {session.tags ? session.tags.split(',').filter(Boolean).map(tag => (
                                                    <Chip key={tag} label={t(TAG_LABEL_MAP[tag] || tag)} size="small" variant="outlined" />
                                                )) : '—'}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap>
                                                {session.recorded_by_name || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                                {session.notes || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); setDeleteTarget(session.id); }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>

            <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>{t('SESSIONS.DELETE_TITLE')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('SESSIONS.DELETE_BODY')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>{t('SESSIONS.DELETE_CANCEL')}</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>{t('SESSIONS.DELETE_CONFIRM')}</Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
